import { personalSignatureRequired as configuredPersonalSignatureRequired } from '@kop/operations-firebase-adapter';

import type {
  CanonicalOnboardingDraft,
  CanonicalOnboardingProjection,
  CanonicalOnboardingScope,
} from './types';

/**
 * Pure onboarding draft logic. The server owns organisation, site, contractor,
 * persona and capabilities; everything here only shapes the fields a person is
 * allowed to fill and keeps the version / consent envelope honest.
 */

export const CONTACT_PURPOSE = 'urgent-safety-contact' as const;

/** Matches the server identifier grammar used for idempotency keys. */
export const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;

/** Matches the server private-contact grammar: E.164, no separators. */
export const CONTACT_PHONE_PATTERN = /^\+[1-9][0-9]{7,14}$/u;

export type OnboardingContactIntent =
  /** No private contact registered and none being registered now. */
  | { mode: 'none' }
  /** A contact is already stored server-side; the number never returns here. */
  | { mode: 'retained' }
  /** A new number is being registered in this session. */
  | { mode: 'register'; phone: string; consent: boolean };

export type OnboardingFormState = {
  displayName: string;
  jobTitle: string;
  jobGrade: string;
  contact: OnboardingContactIntent;
  notifications: readonly { topic: string; enabled: boolean }[];
  defaultScopeId: string;
  areaIds: readonly string[];
  workCategoryIds: readonly string[];
  homePreferences: { compact: boolean; favoriteFeatureIds: readonly string[] };
  signature: { assetId: string; contentSha256: string; version: number } | null;
};

export type OnboardingStepId = 'scope' | 'profile' | 'contact' | 'notifications' | 'signature';

export type OnboardingIssue = { step: OnboardingStepId; message: string };

export type OnboardingFailure = {
  code: string;
  message: string;
  /** `reload` means the client must re-read the server before writing again. */
  recovery: 'reload' | 'retry' | 'none';
};

/**
 * One procedure, in the order a person walks it.
 *
 * The last step is not part of the onboarding draft: the server refuses a
 * personal signature until `canonicalOnboardingHeads/{uid}.status` is already
 * `complete`, and refuses a draft save once it is. The client keeps the server's
 * approved home defaults in the draft, but does not make people configure the
 * workspace layout during first-time setup.
 */
export const onboardingSteps: readonly { id: OnboardingStepId; label: string }[] = [
  { id: 'scope', label: '소속 확인' },
  { id: 'profile', label: '담당자 정보' },
  { id: 'signature', label: '서명 등록' },
];

/** The steps that make up the onboarding draft, i.e. everything before the server milestone. */
export const onboardingFormSteps = onboardingSteps.filter((step) => step.id !== 'signature');

/** Position of the signature step in the rail. */
export const signatureStepIndex = onboardingSteps.length - 1;

/**
 * Whether the server would accept a personal signature for this account.
 *
 * The Firebase adapter validates the server-issued default scope against the
 * projected organization site. Reuse that exact resolver here so the form's
 * same-session transition cannot diverge from login/session admission.
 */
export function personalSignatureRequired(projection: CanonicalOnboardingProjection): boolean {
  return configuredPersonalSignatureRequired(projection);
}

export function scopeById(
  projection: CanonicalOnboardingProjection,
  scopeId: string,
): CanonicalOnboardingScope | null {
  return projection.approved.scopes.find((scope) => scope.scopeId === scopeId) ?? null;
}

function keepApproved(values: readonly string[], approved: readonly string[]): readonly string[] {
  return [...new Set(values.filter((value) => approved.includes(value)))];
}

/**
 * Normalises Korean phone input to the E.164 form the server validates.
 * Returns an empty string when the input cannot be normalised.
 */
export function normalizeContactPhone(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return '';
  const international = trimmed.startsWith('+');
  const digits = trimmed.replace(/[^0-9]/gu, '');
  if (digits.length === 0) return '';
  const candidate = international
    ? `+${digits}`
    : digits.startsWith('82')
      ? `+${digits}`
      : digits.startsWith('0')
        ? `+82${digits.slice(1)}`
        : `+82${digits}`;
  return CONTACT_PHONE_PATTERN.test(candidate) ? candidate : '';
}

/**
 * Builds editable state from the server projection. A saved draft is resumed;
 * scope-bound work categories deliberately start empty because basic onboarding
 * no longer lets a person choose work assignments. This leaves existing draft
 * records readable while preventing a hidden prior choice from being replayed
 * on the next explicit draft save.
 */
export function onboardingFormFromProjection(
  projection: CanonicalOnboardingProjection,
  fallbackDisplayName = '',
): OnboardingFormState {
  const draft = projection.state.draft;
  const approvedIds = projection.approved.scopes.map((scope) => scope.scopeId);
  const defaultScopeId =
    draft && approvedIds.includes(draft.defaultScopeId)
      ? draft.defaultScopeId
      : approvedIds.includes(projection.approved.defaultScopeId)
        ? projection.approved.defaultScopeId
        : (approvedIds[0] ?? '');
  const scope = scopeById(projection, defaultScopeId);
  const topics = projection.options.notificationTopics;
  const saved = new Map((draft?.notifications ?? []).map((item) => [item.topic, item.enabled]));
  const notifications = [
    ...topics.map((topic) => ({ topic, enabled: saved.get(topic) ?? true })),
    ...(draft?.notifications ?? []).filter((item) => !topics.includes(item.topic)),
  ];
  return {
    displayName: (
      draft?.displayName ??
      projection.profile?.displayName ??
      fallbackDisplayName
    ).trim(),
    jobTitle: draft?.jobTitle ?? projection.profile?.jobTitle ?? '',
    jobGrade: draft?.jobGrade ?? projection.profile?.jobGrade ?? '',
    contact:
      draft?.privateContact?.configured === true || projection.profile?.contactConfigured === true
        ? { mode: 'retained' }
        : { mode: 'none' },
    notifications,
    defaultScopeId,
    areaIds: keepApproved(draft?.areaIds ?? [], scope?.areaIds ?? []),
    workCategoryIds: [],
    homePreferences: {
      compact: draft?.homePreferences.compact ?? projection.options.homePreferences.compact,
      favoriteFeatureIds: [
        ...new Set(
          draft?.homePreferences.favoriteFeatureIds ??
            projection.options.homePreferences.favoriteFeatureIds,
        ),
      ],
    },
    signature: draft?.signature ?? null,
  };
}

/**
 * Changing the default scope re-filters the scope-bound lists. Work categories
 * remain empty because basic onboarding does not author work assignments.
 */
export function withDefaultScope(
  form: OnboardingFormState,
  projection: CanonicalOnboardingProjection,
  scopeId: string,
): OnboardingFormState {
  const scope = scopeById(projection, scopeId);
  if (!scope) return form;
  return {
    ...form,
    defaultScopeId: scope.scopeId,
    areaIds: keepApproved(form.areaIds, scope.areaIds),
    workCategoryIds: [],
  };
}

export function toggleNotification(
  form: OnboardingFormState,
  topic: string,
  enabled: boolean,
): OnboardingFormState {
  return {
    ...form,
    notifications: form.notifications.map((item) =>
      item.topic === topic ? { topic, enabled } : item,
    ),
  };
}

export function toggleFavoriteFeature(
  form: OnboardingFormState,
  featureId: string,
): OnboardingFormState {
  const favorites = form.homePreferences.favoriteFeatureIds;
  return {
    ...form,
    homePreferences: {
      ...form.homePreferences,
      favoriteFeatureIds: favorites.includes(featureId)
        ? favorites.filter((value) => value !== featureId)
        : [...favorites, featureId],
    },
  };
}

/**
 * A private contact is only ever sent together with an explicit consent and the
 * single purpose the server models. Consent without a number, or a number
 * without consent, is a validation issue rather than a silent partial write.
 */
export function contactIssues(contact: OnboardingContactIntent): readonly OnboardingIssue[] {
  if (contact.mode !== 'register') return [];
  const phone = normalizeContactPhone(contact.phone);
  const issues: OnboardingIssue[] = [];
  if (contact.phone.trim().length > 0 && phone.length === 0)
    issues.push({ step: 'contact', message: '연락처 형식을 확인해 주세요. 예) 010-1234-5678' });
  if (phone.length > 0 && !contact.consent)
    issues.push({
      step: 'contact',
      message: '긴급 안전 연락 목적의 제공 동의를 선택해야 연락처를 등록할 수 있습니다.',
    });
  if (contact.consent && phone.length === 0 && contact.phone.trim().length === 0)
    issues.push({
      step: 'contact',
      message: '동의만으로는 등록할 수 없습니다. 연락처를 입력해 주세요.',
    });
  return issues;
}

export function onboardingIssues(
  form: OnboardingFormState,
  projection: CanonicalOnboardingProjection,
): readonly OnboardingIssue[] {
  const issues: OnboardingIssue[] = [];
  if (!scopeById(projection, form.defaultScopeId))
    issues.push({ step: 'scope', message: '승인된 업무 범위를 선택해 주세요.' });
  const displayName = form.displayName.trim();
  if (displayName.length < 2 || displayName.length > 100)
    issues.push({ step: 'profile', message: '이름은 2자 이상 100자 이하로 입력해 주세요.' });
  if (form.jobTitle.trim().length === 0)
    issues.push({ step: 'profile', message: '담당업무를 입력해 주세요.' });
  if (form.jobTitle.trim().length > 100)
    issues.push({ step: 'profile', message: '담당업무는 100자 이하로 입력해 주세요.' });
  if (form.jobGrade.trim().length > 100)
    issues.push({ step: 'profile', message: '직급은 100자 이하로 입력해 주세요.' });
  if (form.contact.mode === 'none')
    issues.push({ step: 'contact', message: '비상 연락용 전화번호를 등록해 주세요.' });
  issues.push(...contactIssues(form.contact));
  return issues;
}

export function stepIssues(
  form: OnboardingFormState,
  projection: CanonicalOnboardingProjection,
  step: OnboardingStepId,
): readonly OnboardingIssue[] {
  return onboardingIssues(form, projection).filter(
    (issue) => issue.step === step || (step === 'profile' && issue.step === 'contact'),
  );
}

/**
 * Serialises exactly the fields the server accepts. `configured` and
 * `consentedAt` come back on a resumed draft and must never be echoed: the
 * server rejects unknown keys inside `privateContact`.
 */
export function toOnboardingSavePayload(form: OnboardingFormState): CanonicalOnboardingDraft {
  const phone = form.contact.mode === 'register' ? normalizeContactPhone(form.contact.phone) : '';
  const registerContact =
    form.contact.mode === 'register' && phone.length > 0 && form.contact.consent;
  const draft: CanonicalOnboardingDraft = {
    displayName: form.displayName.trim(),
    jobTitle: form.jobTitle.trim(),
    jobGrade: form.jobGrade.trim(),
    notifications: form.notifications.map((item) => ({
      topic: item.topic,
      enabled: item.enabled,
    })),
    defaultScopeId: form.defaultScopeId,
    areaIds: [...new Set(form.areaIds)],
    workCategoryIds: [...new Set(form.workCategoryIds)],
    homePreferences: {
      compact: form.homePreferences.compact,
      favoriteFeatureIds: [...new Set(form.homePreferences.favoriteFeatureIds)],
    },
  };
  if (registerContact) draft.privateContact = { phone, consent: true, purpose: CONTACT_PURPOSE };
  if (form.signature) draft.signature = { ...form.signature };
  return draft;
}

/** True when the pending edits differ from what the server already stores. */
export function hasUnsavedChanges(
  form: OnboardingFormState,
  projection: CanonicalOnboardingProjection,
): boolean {
  if (projection.state.draft === null) return true;
  if (form.contact.mode === 'register') return true;
  const next = toOnboardingSavePayload(form);
  const stored = projection.state.draft;
  return (
    next.displayName !== stored.displayName ||
    next.jobTitle !== stored.jobTitle ||
    next.jobGrade !== (stored.jobGrade ?? '') ||
    next.defaultScopeId !== stored.defaultScopeId ||
    JSON.stringify(next.notifications) !== JSON.stringify(stored.notifications) ||
    JSON.stringify(next.areaIds) !== JSON.stringify(stored.areaIds) ||
    JSON.stringify(next.workCategoryIds) !== JSON.stringify(stored.workCategoryIds) ||
    JSON.stringify(next.homePreferences) !== JSON.stringify(stored.homePreferences)
  );
}

/**
 * One key per (kind, version) attempt. A successful write moves the version on,
 * so the next write rotates the key; a retry of the same attempt replays.
 */
export function idempotencySeed(): string {
  const source = globalThis.crypto;
  if (source && typeof source.randomUUID === 'function') return source.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function createIdempotencyKey(
  kind: 'save' | 'complete',
  version: number,
  seed: string,
): string {
  const safeSeed = seed.replace(/[^A-Za-z0-9._:-]/gu, '').slice(0, 64) || 'seed';
  return `onb-${kind}-v${Math.max(0, Math.trunc(version))}-${safeSeed}`;
}

function failureCode(error: unknown): string {
  if (typeof error !== 'object' || error === null) return '';
  const details = (error as { details?: unknown }).details;
  if (typeof details === 'object' && details !== null && 'code' in details)
    return String((details as { code: unknown }).code ?? '');
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : '';
}

const failureCopy: Record<string, { message: string; recovery: OnboardingFailure['recovery'] }> = {
  'storage/unauthorized': {
    message:
      '\uC11C\uBA85 \uD30C\uC77C\uC744 \uC800\uC7A5\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uD654\uBA74\uC744 \uC0C8\uB85C\uACE0\uCE68\uD55C \uB4A4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694. \uACC4\uC18D \uBC1C\uC0DD\uD558\uBA74 \uAD00\uB9AC\uC790\uC5D0\uAC8C \uC54C\uB824 \uC8FC\uC138\uC694.',
    recovery: 'none',
  },
  VERSION_CONFLICT: {
    message:
      '다른 기기에서 먼저 저장되어 화면의 내용이 최신 기록보다 오래되었습니다. 최신 내용을 다시 불러온 뒤 이어서 작성해 주세요.',
    recovery: 'reload',
  },
  IDEMPOTENCY_CONFLICT: {
    message:
      '같은 요청 번호로 다른 내용이 이미 저장되었습니다. 덮어쓰지 않았습니다. 최신 내용을 다시 불러와 확인해 주세요.',
    recovery: 'reload',
  },
  SCOPE_MISMATCH: {
    message:
      '승인된 소속과 업무 범위가 변경되었습니다. 최신 내용을 다시 불러온 뒤 범위를 확인해 주세요.',
    recovery: 'reload',
  },
  SELF_AUTHORITY_EDIT_FORBIDDEN: {
    message: '소속·역할·권한은 승인된 설정을 따르며 이 화면에서는 변경할 수 없습니다.',
    recovery: 'reload',
  },
  FORBIDDEN: {
    message: '승인된 업무 범위가 아직 없습니다. 관리자 승인 후 다시 시도해 주세요.',
    recovery: 'none',
  },
  SIGNATURE_ASSET_INVALID: {
    message: '등록된 서명을 확인하지 못했습니다.',
    recovery: 'reload',
  },
  VALIDATION_FAILED: {
    message: '입력 값을 다시 확인해 주세요.',
    recovery: 'retry',
  },
  ONBOARDING_REQUIRED: {
    message:
      '시작 준비가 아직 완료되지 않았습니다. 최신 내용을 다시 불러온 뒤 이어서 진행해 주세요.',
    recovery: 'reload',
  },
  SIGNATURE_EMPTY: {
    message: '아직 서명이 그려지지 않았습니다. 서명란에 서명을 그려 주세요.',
    recovery: 'retry',
  },
  SIGNATURE_CANVAS_UNAVAILABLE: {
    message: '이 브라우저에서 서명란을 열지 못했습니다. 다른 브라우저에서 다시 시도해 주세요.',
    recovery: 'retry',
  },
  SIGNATURE_ENCODE_FAILED: {
    message: '그린 서명을 저장 형식으로 변환하지 못했습니다. 지우고 다시 그려 주세요.',
    recovery: 'retry',
  },
  SIGNATURE_FORMAT_INVALID: {
    message: '서명은 WebP 형식으로만 등록됩니다. 지우고 다시 그려 주세요.',
    recovery: 'retry',
  },
  SIGNATURE_SIZE_INVALID: {
    message: '서명이 서명란을 벗어났습니다. 지우고 서명란 안에 다시 그려 주세요.',
    recovery: 'retry',
  },
  SIGNATURE_DIMENSIONS_INVALID: {
    message: '서명 크기가 등록 기준에 맞지 않습니다. 지우고 다시 그려 주세요.',
    recovery: 'retry',
  },
  SIGNATURE_IMAGE_INVALID: {
    message: '서명 이미지를 확인하지 못했습니다. 지우고 다시 그려 주세요.',
    recovery: 'retry',
  },
  SIGNATURE_STORAGE_UNCONFIGURED: {
    message: '서명 저장소 설정이 배포에 포함되지 않아 등록할 수 없습니다. 관리자에게 알려 주세요.',
    recovery: 'none',
  },
  SIGNATURE_INTENT_INVALID: {
    message: '서명을 등록할 준비를 마치지 못했습니다. 잠시 후 다시 시도해 주세요.',
    recovery: 'retry',
  },
  MALWARE_SCAN_REQUIRED: {
    message: '업로드한 파일의 검사 결과를 아직 받지 못했습니다. 잠시 기다린 뒤 다시 시도해 주세요.',
    recovery: 'retry',
  },
  EVIDENCE_NOT_COMMITTED: {
    message: '등록한 서명을 확인하지 못했습니다. 다시 등록해 주세요.',
    recovery: 'retry',
  },
  SIGNATURE_COMMIT_FAILED: {
    message: '서명이 확정되지 않았습니다. 다시 등록해 주세요.',
    recovery: 'retry',
  },
  MY_INFO_PROJECTION_INVALID: {
    message: '등록 상태를 확인하지 못했습니다. 다시 불러와 확인해 주세요.',
    recovery: 'reload',
  },
  SETUP_PROBE_UNAVAILABLE: {
    message: '준비 상태를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.',
    recovery: 'retry',
  },
  ONBOARDING_RECEIPT_MISSING: {
    message: '완료 여부를 확인하지 못해 처리를 보류했습니다. 잠시 후 다시 시도해 주세요.',
    recovery: 'reload',
  },
};

/** Turns a server failure into a real message; never a silent overwrite. */
export function describeOnboardingFailure(error: unknown): OnboardingFailure {
  const code = failureCode(error);
  const known = failureCopy[code];
  if (known) return { code, ...known };
  if (code === 'functions/unauthenticated' || code === 'unauthenticated')
    return {
      code,
      message: '로그인이 만료되었습니다. 다시 로그인해 주세요.',
      recovery: 'none',
    };
  return {
    code: code || 'UNKNOWN',
    message: '저장하지 못했습니다. 연결을 확인한 뒤 다시 시도해 주세요.',
    recovery: 'retry',
  };
}
